import React, { useEffect, useState } from "react";
import { PageHeader } from "../components/Layout";
import { api, apiErr } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { Database, Copy, Download, Server, Info as InfoIcon, RefreshCw, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { WifiBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";

function SqlStatusCard() {
  const { isSuperAdmin } = useAuth();
  const [st, setSt] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = () => api.get("/freeradius/sql/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const syncAll = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/freeradius/sql/sync-all");
      toast.success(`Sincronizzati ${r.data.synced} codici attivi su FreeRADIUS SQL`);
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail, "Errore sincronizzazione"));
    } finally {
      setSyncing(false);
    }
  };

  if (!st) return null;
  const c = st.counts || {};

  return (
    <div className="rounded-xl border border-[#1F2937] bg-[#111827] p-6 mb-4" data-testid="radius-sql-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Database className="h-4 w-4 text-emerald-400" />
          <h3 className="font-display font-semibold">Integrazione SQL reale (rlm_sql)</h3>
          {st.enabled && st.connected && <span className="inline-flex items-center gap-1 text-xs text-emerald-400 bg-emerald-500/10 ring-1 ring-emerald-500/20 rounded-full px-2 py-0.5"><CheckCircle2 className="h-3 w-3" /> Connesso</span>}
          {st.enabled && !st.connected && <span className="inline-flex items-center gap-1 text-xs text-rose-400 bg-rose-500/10 ring-1 ring-rose-500/20 rounded-full px-2 py-0.5"><XCircle className="h-3 w-3" /> Errore connessione</span>}
          {!st.enabled && <span className="inline-flex items-center gap-1 text-xs text-slate-400 bg-slate-500/10 ring-1 ring-slate-500/20 rounded-full px-2 py-0.5">Non configurato</span>}
        </div>
        {isSuperAdmin && st.enabled && (
          <Button size="sm" onClick={syncAll} disabled={syncing || !st.connected} data-testid="radius-sql-sync-btn">
            {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />} Sincronizza codici attivi
          </Button>
        )}
      </div>
      {!st.enabled ? (
        <p className="text-sm text-slate-400 leading-relaxed">
          Il pannello scrive automaticamente i codici Wi-Fi nelle tabelle standard FreeRADIUS (<span className="font-mono text-cyan-400">radcheck</span>, <span className="font-mono text-cyan-400">radreply</span>) quando configuri la variabile d'ambiente <span className="font-mono text-amber-300">RADIUS_SQL_URL</span> (MySQL/MariaDB o PostgreSQL). Finché non è configurata, l'autenticazione resta in modalità simulata.
        </p>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 text-xs">
            <Field label="Driver" value={st.driver} />
            <Field label="Connessione" value={st.url} />
            <Field label="radcheck" value={c.radcheck ?? 0} />
            <Field label="radreply" value={c.radreply ?? 0} />
            <Field label="Utenti" value={c.users ?? 0} />
          </div>
          {st.error && <div className="mt-2 rounded-lg bg-rose-600/10 ring-1 ring-rose-600/20 px-3 py-2 text-xs text-rose-300 font-mono">{st.error}</div>}
          <p className="text-xs text-slate-500 mt-3">I codici attivi vengono scritti/rimossi automaticamente al check-in e check-out. Usa "Sincronizza" per riallineare tutti i codici attivi.</p>
        </>
      )}
    </div>
  );
}

export default function FreeRadius() {
  const { t } = useI18n();
  const { effectiveStructure } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => {
    const p = effectiveStructure ? `?structure_id=${effectiveStructure}` : "";
    api.get(`/freeradius/config${p}`).then((r) => setD(r.data)).catch(() => {});
  }, [effectiveStructure]);

  if (!d) return <div className="text-slate-400">{t("common.loading")}</div>;
  const copy = (v) => { navigator.clipboard.writeText(v); toast.success(t("frd.copy") + " ✓"); };
  const dl = (name, v) => { const b = new Blob([v], { type: "text/plain" }); const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = name; a.click(); URL.revokeObjectURL(u); };

  return (
    <div data-testid="freeradius-page">
      <PageHeader title={t("frd.title")} subtitle={t("frd.subtitle")} />

      <SqlStatusCard />

      <div className="rounded-xl border border-[#1F2937] bg-[#111827] p-6 mb-4">
        <div className="flex items-center gap-2 mb-2"><InfoIcon className="h-4 w-4 text-amber-400" /><h3 className="font-display font-semibold">{t("frd.how_title")}</h3></div>
        <p className="text-sm text-slate-400 leading-relaxed">{t("frd.how_desc")}</p>
        <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
          <Field label="Host" value={d.server.host} onCopy={() => copy(d.server.host)} />
          <Field label="Auth Port" value={d.server.auth_port} />
          <Field label="Acct Port" value={d.server.acct_port} />
          <Field label="Backend" value={d.server.sql_backend} />
        </div>
        <div className="mt-2 rounded-lg bg-blue-600/10 ring-1 ring-blue-600/20 px-3 py-2 text-xs text-blue-300 font-mono">REST authorize: {d.authorize_endpoint}</div>
      </div>

      {/* clients.conf */}
      <div className="rounded-xl border border-[#1F2937] bg-[#111827] p-6 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><Server className="h-4 w-4 text-cyan-400" /><h3 className="font-display font-semibold">{t("frd.clients")}</h3></div>
          <div className="flex gap-2">
            <button data-testid="frd-copy-clients" onClick={() => copy(d.clients_conf)} className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 px-3 py-1.5 text-xs font-medium"><Copy className="h-3.5 w-3.5" /> {t("frd.copy")}</button>
            <button data-testid="frd-dl-clients" onClick={() => dl("clients.conf", d.clients_conf)} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600/20 text-blue-300 ring-1 ring-blue-600/30 hover:bg-blue-600/30 px-3 py-1.5 text-xs font-medium"><Download className="h-3.5 w-3.5" /> clients.conf</button>
          </div>
        </div>
        <pre className="rounded-lg bg-[#0B0F17] border border-[#1F2937] p-4 text-xs font-mono text-emerald-300 whitespace-pre-wrap overflow-x-auto max-h-64 overflow-y-auto">{d.clients_conf}</pre>
      </div>

      {/* radcheck users */}
      <div className="rounded-xl border border-[#1F2937] bg-[#111827] overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-[#1F2937]"><Database className="h-4 w-4 text-blue-400" /><h3 className="font-display font-semibold">{t("frd.users")} <span className="text-slate-500 font-normal">({d.users.length})</span></h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-[#1F2937]">
              <th className="px-4 py-3">{t("frd.username")}</th><th className="px-4 py-3">Attribute</th><th className="px-4 py-3">Op</th><th className="px-4 py-3">{t("frd.password")}</th><th className="px-4 py-3">{t("common.status")}</th><th className="px-4 py-3">{t("nav.structures")}</th>
            </tr></thead>
            <tbody>
              {d.users.map((u, i) => (
                <tr key={i} data-testid={`radcheck-row-${i}`} className="border-b border-[#1F2937]/60 hover:bg-white/[0.02]">
                  <td className="px-4 py-2.5 font-mono font-semibold text-cyan-400">{u.username}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-400">{u.attribute}</td>
                  <td className="px-4 py-2.5 font-mono text-slate-500">{u.op}</td>
                  <td className="px-4 py-2.5 font-mono text-emerald-300">{u.value}</td>
                  <td className="px-4 py-2.5"><WifiBadge status={u.status} /></td>
                  <td className="px-4 py-2.5 text-slate-400">{u.structure}</td>
                </tr>
              ))}
              {d.users.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">{t("common.none")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onCopy }) {
  return (
    <div className="rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center justify-between">{label}{onCopy && <button onClick={onCopy} className="text-slate-500 hover:text-blue-400"><Copy className="h-3 w-3" /></button>}</div>
      <div className="font-mono text-cyan-400 truncate">{value}</div>
    </div>
  );
}
